import { captureException } from '@sentry/react'
import { app_domain, development } from 'app/config/constants'
import { action, autorun, makeAutoObservable, reaction, runInAction } from 'mobx'
import { api } from 'store/api'
import { BasicStore } from 'store/basic'
import { controller } from 'store/controller'
import { FrameStore } from 'store/frame'
import { FullscreenStore } from 'store/fullscreen'
import { ExistingStore } from 'store/generation/ExistingStore'
import { GenerateParams } from 'store/generation/generateLook'
import { GeneratingStore } from 'store/generation/GeneratingStore'
import { InferenceCarouselStore } from 'store/generation/InferenceCarouselStore'
import { InferencesStore } from 'store/inference/InferencesStore'
import { UserDataBannerStore } from 'store/inference/UserDataBannerStore'
import { item_main, ItemStore } from 'store/item/ItemStore'
import { me } from 'store/me'
import { opt } from 'store/opt'
import { getBasicFirstStep, getBasicStepForType } from 'store/profile/BasicForm'
import { IdentitiesStore } from 'store/profile/IdentitiesStore'
import ProfileStore from 'store/profile/ProfileStore'
import { stat } from 'store/stat'
import { ErrorType } from 'type/ErrorType'
import { IdentityConfig } from 'type/IdentityConfig'
import { Item } from 'type/Item'
import { Limit } from 'type/Limit'
import { Meta, OpenParams, Options, Photo } from 'type/Options'
import { logUser } from 'util/adminDebug'
import { skipAsync } from 'util/async'
import { isNativeWebView, isSafari } from 'util/browser'
import { postMessage, sendEvent } from 'util/message'
import { isMobileBrowser } from 'util/mobile'
import { getTgWebAppData } from 'util/tg'

export type ScreenName =
  | 'start'
  | 'start_combo'
  | 'combine_select'
  | 'profile'
  | 'generating'
  | 'existing'
  | 'image'
  | 'generations'
  | 'policy'
  | 'selfie'
  | 'fullbody'
  | 'preload'
export type ScreenTabName = 'favorites' | 'outfits' | 'profiles'
export type ProfileBackAction = 'generate' | 'combine'

export type FrameState = 'none' | 'min' | 'max'

class MainStore {
  // means i18n is ready
  init = false
  error?: ErrorType
  busy = false
  private _fullscreen?: FullscreenStore
  setupDone = false
  options?: Options
  meta?: Meta
  private _state: FrameState = 'none'
  private _screen: ScreenName = 'generations'
  open_item_id?: string
  identities = new IdentitiesStore()
  readonly banner = new UserDataBannerStore()
  createPhoto: Photo = 'selfie'
  createProfile = false
  // Basic: генерация запрошена, но ещё не стартовала (сохраняем профиль).
  // Всё это время держим экран генерации, иначе мелькает прошлый результат.
  basicPending = false
  generating!: GeneratingStore
  existing!: ExistingStore
  carousel!: InferenceCarouselStore
  inferences = new InferencesStore()
  parentWidth = window.innerWidth
  mobile = false
  safari = isSafari()
  nativeWebView = isNativeWebView()
  frame = new FrameStore()
  profileBack?: ProfileBackAction
  policyGenerateParams?: GenerateParams
  private _limit: Limit[] = []
  comboItemId?: string
  comboItem?: ItemStore
  tempComboItem?: ItemStore
  private _doc = ''
  closeProfileCallback?: () => unknown
  customBuy: boolean = false
  manualOpenProfile = false
  basic!: BasicStore

  constructor() {
    makeAutoObservable(this)
    this.updateMobile()
    if (development) setInterval(action(() => this.updateMobile()), 100)
    autorun(() => {
      if (!this.profile || !this.item) return
      const gender = this.profile.gender
      this.item.rec.male = gender === 'male'
    })
    autorun(() => this.hideWhenNoLimit())
  }

  private hideWhenNoLimit(): any {
    if (this.hide_when_no_limit) this.state = 'none'
  }

  private get hide_when_no_limit(): boolean {
    if (!this.options?.hide_when_no_limit) return false
    const ITEM = ['COMPANY_PRODUCT_DAY', 'ACCESS_KEY_PRODUCT_DAY']
    const exceeded = this._limit.find(it => !ITEM.includes(it.limit_type) && it.exceeded)
    return !!exceeded
  }

  get item(): ItemStore | undefined {
    return item_main.optional
  }

  set item(value: ItemStore | undefined) {
    item_main.it = value
  }

  get profiles(): boolean {
    return !opt.stand && !opt.single && this.identities.profiles.some(it => !!it.identity_id)
  }

  get rec() {
    const item = this.tempComboItem || this.item!
    return item.rec
  }

  get fullscreen(): FullscreenStore | undefined {
    return this._fullscreen
  }

  set fullscreen(fullscreen: FullscreenStore | undefined) {
    this._fullscreen = fullscreen
    postMessage('fullscreen', { fullscreen: !!fullscreen })
  }

  get screen(): ScreenName {
    return this._screen
  }

  get available(): boolean {
    return this._limit.length === 0 || !this._limit.some(l => l.exceeded)
  }

  get exceededLimitType(): string | undefined {
    return this._limit.find(l => l.exceeded)?.limit_type
  }

  get screenTab(): ScreenTabName | undefined {
    if (this.screen === 'profile') return 'profiles'
    const outfits = this.screen === 'generations' || this.screen === 'image'
    if (!outfits) return undefined
    const fav = this.inferences.tab === 'fav'
    return fav ? 'favorites' : 'outfits'
  }

  set screen(screen: ScreenName) {
    if (opt.basic) {
      const allowed: ScreenName[] = ['preload', 'selfie', 'fullbody', 'profile', 'generating', 'image']
      if (!allowed.includes(screen)) screen = 'profile'
      if (screen === 'profile' && this.profile) {
        const { basic, type } = this.profile
        basic.step = basic.done ? getBasicStepForType(type) : getBasicFirstStep()
      }
    }

    this._screen = screen
    if (this.profile) store.profile.step = 'profile'
    if (this.profile) this.profile.simple.stage = this.profile.identity_id ? 'profile' : 'selfie'
    if (this.item) this.item.step = 'none'
    this.inferences.buy = undefined
    this.banner.cancel()
    if (screen !== 'profile') {
      this.manualOpenProfile = false
      this.identities.reset()
    }
  }

  get tg(): boolean {
    return 'TelegramWebviewProxy' in window || !!window.Telegram?.WebApp
  }

  get tgWebAppData(): string {
    const hash = this.meta?.hash
    return getTgWebAppData(hash)
  }

  async reset() {
    this.init = false
    this.screen = opt.basic ? 'preload' : 'policy'
    this.identities = new IdentitiesStore()
    this.banner.reset()
    this.generating = undefined!
    this.carousel = undefined!
    this.inferences = new InferencesStore()
    this.open_item_id = undefined
    this.item = undefined
    this.comboItemId = undefined
    this.comboItem = undefined
    this.tempComboItem = undefined
    await this.setup()
    runInAction(() => (this.init = true))
  }

  async resetRemoteData() {
    await Promise.allSettled([
      api.updateUser({ email: '', phone: '', pic: '', personal_data_consent: 0 }),
      this.identities.deleteAll(),
      this.inferences.deleteAll(),
    ])
  }

  get needEmail(): boolean {
    return !opt.inline && !opt.stand && !!this.options?.email?.on
  }

  get title() {
    if (!this.init) return ''
    if (app_domain === 'atelier') return 'Atelier Digital'
    if (app_domain === 'showoff') return 'Showoff'
    return 'Dresscode AI'
  }

  get link() {
    if (!this.init) return ''
    if (app_domain === 'atelier') return 'https://atelierdigitalai.com/'
    if (app_domain === 'showoff') return 'https://www.showoff.app/'
    return 'https://www.dresscode.ai/'
  }

  get total(): number {
    return this.inferences.inferences.length
  }

  get count(): number {
    return this.inferences.inferences.reduce((sum, it) => {
      if (it.viewed || it.error) return sum
      return sum + 1
    }, 0)
  }

  get ready() {
    return this.setupDone
  }

  get loader() {
    const complete = this.inferences.inferences.every(it => it.complete || it.error)
    return !this.ready || !complete
  }

  get state(): FrameState {
    return this._state
  }

  set state(state: FrameState) {
    const name = state === 'max' ? 'on-open-widget' : 'on-close-widget'
    sendEvent(name, {})
    postMessage('state', { state })
    this._state = state
  }

  get identity_id(): string | undefined {
    return this.profile?.identity_id || undefined
  }

  get profile(): ProfileStore {
    return this.identities.profile
  }

  async setup() {
    reaction(
      () => this.count,
      count => postMessage('count', { count }),
    )

    const _config = api.getIdentityConfig()
    const { user, metrics } = await this.getMe(opt.boutique || opt.inline)
    logUser('store.setup after getMe', { hasUser: !!user, user_id: user?.user_id ?? null, local_user: me.user })

    if (opt.basic) this.basic = new BasicStore()

    // let user_id = data?.user_id
    // привязываем user_id к telegram_id или получаем user_id, если он уже привязан
    // const telegram_id = getTelegramIdFromMiniApp(this.tgWebAppData)
    // if (telegram_id) {
    //   const result = await api.setOrGetTelegramUserId(data?.user_id!, telegram_id)
    //   if (result?.user_id) {
    //     user_id = result.user_id
    //   }
    // }

    if (user) {
      me.init(user, metrics)

      if (!opt.meta.selfie) void this.inferences.load()
      const identities = await api.getIdentities({ user_id: user.user_id })
      const config = await _config
      if (opt.irnby) this.applyIrnbyLimits(config)

      this.identities.init(config, identities)
    } else {
      const config = await _config
      if (opt.irnby) this.applyIrnbyLimits(config)

      this.identities.init(config, [])
    }

    autorun(() => {
      const user = !!me.user_id
      const hide = !!this.options?.hide_when_no_limit
      if (user || hide) void this.checkLimit()
    })
  }

  async create() {
    const { user, metrics } = await this.getMe(true)
    if (!user) throw new Error('no user')

    me.init(user, metrics)
    void this.inferences.load()
  }

  private async getMe(force: boolean) {
    const initData = this.tgWebAppData
    return await api.getMe(force, initData)
  }

  getState(params?: OpenParams) {
    const { item, autostart, combo_item_id } = params ?? {}
    const no_limit = !!this.profile?.identity_id && !this.available

    if (opt.basic) return 'max'

    if (combo_item_id && me.policy && me.user_id && !no_limit) {
      const profile = !!this.profile?.identity_id
      return profile ? 'min' : 'max'
    }

    if (item && autostart && me.policy && me.user_id) {
      return 'min'
    }

    return 'max'
  }

  readonly open = async (params?: OpenParams) => {
    const { item, favorites, profile, combo_item_id, autostart } = params ?? {}
    this.open_item_id = item

    await this.isolated(
      () => {
        if (opt.basic) me.policy = true

        const noSavedProfiles = !this.identities.profiles.some(p => !!p.identity_id)
        if (noSavedProfiles && opt.photo.length > 1) {
          this.identities.profile.isNew = false
          this.identities.profile.type = opt.photo_def
        }

        this.error = undefined
        this.state = this.getState(params)

        if (opt.basic) {
          this.screen = 'preload'
        } else if (!me.policy) {
          this.screen = 'policy'
        } else if (combo_item_id && !me.user_id) {
          this.screen = 'profile'
          this.profileBack = 'combine'
        } else {
          this.screen = item ? 'start' : me.user_id ? 'generations' : 'profile'
        }

        if (favorites) {
          const fav = this.inferences.listFav.length > 0
          if (me.user_id) {
            this.inferences.tab = fav ? 'fav' : 'all'
            controller.openInference()
          } else {
            controller.openStart()
          }
        }

        if (profile) {
          if (me.user_id) {
            controller.openProfile()
          } else {
            controller.openStart()
          }
        }

        this.comboItemId = combo_item_id
        this.comboItem = undefined
        this.tempComboItem = undefined
      },
      async () => {
        if (opt.stand) await me.ensure

        if (item && !this.same(item)) {
          const it = await this.loadItem(item)
          if (it) this.sendOpenStat(it)
        }
        if (combo_item_id) {
          await this.loadComboItem(combo_item_id)
        }

        if (favorites && me.user_id) {
          await this.inferences.reload()
          const fav = this.inferences.listFav.length > 0
          this.inferences.tab = fav ? 'fav' : 'all'
        }

        if (!item && !favorites) {
          await this.inferences.load()
        }

        {
          const { identity_id, saving } = this.profile
          const hasProfile = !!identity_id || !!saving
          if (hasProfile) {
            await this.checkLimit()
          }
          const many = this.identities.profiles.filter(i => i.identity_id || i.saving).length > 1
          const combo = !!opt.options.combine && !!this.item?.combine
          // В basic экрана выбора нет: openStart уводит на шаг профиля. С готовым
          // профилем сразу генерируем по активному, даже если профилей несколько.
          const basicReady = opt.basic && hasProfile && this.available && !this.profile.basicUnfinished
          if (this.comboItemId) {
            if (hasProfile) {
              if (many && !basicReady) {
                this.state = 'max'
                controller.openStart()
              } else {
                if (opt.basic) this.state = 'max'
                void controller.handlePendingComboGeneration()
              }
            } else {
              controller.planGeneration('combine')
            }
          } else if (item && autostart) {
            if (!basicReady && (many || combo || !this.available)) {
              this.state = 'max'
              controller.openStart()
            } else if (this.profile.basicUnfinished) {
              // Профиль не досоздан — вместо генерации возвращаем юзера в его шаг флоу.
              this.state = 'max'
              controller.continueProfile()
            } else if (hasProfile) {
              this.state = opt.basic ? 'max' : 'min'
              opt.basic ? controller.generateBasic() : controller.generateLook()
            } else {
              this.state = 'max'
              controller.planGeneration('generate')
            }
          }
        }

        if (opt.basic && this.screen === 'preload') {
          this.screen = 'profile'
        }
      },
    )
  }

  readonly reopen = async () => {
    await this.isolated(
      () => {
        this.state = 'min'
        this.screen = 'generations'
      },
      () => {
        void this.inferences.load()
      },
    )
  }

  private applyIrnbyLimits(config: IdentityConfig) {
    config.female.dimensions.chest.max = 108
    config.female.dimensions.waist.max = 85
    config.female.dimensions.hips.max = 116

    config.male.dimensions.chest.max = 116
    config.male.dimensions.waist.max = 100
    config.male.dimensions.hips.max = 118
  }

  private isolated = skipAsync(async (before: Function, action: Function, final?: Function) => {
    if (!this.setupDone) {
      console.warn('setup')
      return
    }
    if (this.busy) {
      console.warn('busy')
      return
    }
    runInAction(() => {
      this.busy = true
      this.error = undefined
      before()
    })
    try {
      await action()
    } catch (e) {
      console.error('action failed', e)
      captureException(e)
      this.setError('unknown')
    } finally {
      runInAction(() => {
        this.busy = false
        if (final) final()
      })
    }
  })

  setError(error: ErrorType | undefined) {
    this.error = error
  }

  showUserDataBannerMaybe() {
    if (this.options?.email?.on) void this.banner.showMaybe()
  }

  getLookyShareLink(url: string): string {
    const title = 'Try on'
    const query = new URLSearchParams({ type: 'photo', caption: title, media: url })
    return `rn-looky://navigate/creativity/post?${query}`
  }

  async checkLimit() {
    const item_id = this.item?.json.item_id
    const limit = await api.checkLimit(item_id)
    runInAction(() => this._limit = limit)
  }

  private same(item: string): boolean {
    if (!this.item) return false
    const { item_id, external_item_id } = this.item.json
    return item_id === item || external_item_id === item
  }

  private sendOpenStat(item: Item) {
    const { item_id, collection_id } = item
    stat.open(item_id, collection_id)
  }

  readonly loadItem = skipAsync(async (item_id: string): Promise<Item | undefined> => {
    runInAction(() => (this.item = undefined))
    try {
      const shop = this.options?.shop
      const item = await api.getItem({ item_id, shop })
      runInAction(() => (this.item = new ItemStore(item)))
      return item
    } catch (e) {
      console.error('item loading failed', e)
      captureException(e)
      this.setError('item-not-found')
      return undefined
    }
  })

  readonly loadComboItem = skipAsync(async (combo_item_id: string): Promise<void> => {
    runInAction(() => {
      this.comboItemId = combo_item_id
      this.comboItem = undefined
    })

    try {
      const item = await api.getItem({ item_id: combo_item_id })
      runInAction(() => {
        this.comboItem = new ItemStore(item)
      })
    } catch (e) {
      console.error('item loading failed', e)
      captureException(e)
    }
  })

  private updateMobile() {
    this.mobile = isMobileBrowser()
    document.documentElement.classList.toggle('mobile', this.mobile)
  }

  get doc(): string {
    return this._doc
  }

  set doc(value: string) {
    this._doc = value
  }
}

export const store = new MainStore()
